import * as state from './state.js';
import { showCodePreview, showVisualPreview } from './code-preview.js';
import { attachTerminalSize, refreshPaneLayout, snapPreviewPaneWidth } from './pane-resize.js';
import { updateUrlParams } from './share-link.js';
import { buildErrorContainerHtml, buildUnrangedDiagnosticsStripHtml, buildWarningsStripHtml, updatePreviewStatus } from './preview-status.js';
import {
    closeExamplesModal,
    normalizeExamplePath,
    selectExampleFromGallery,
    showInlineExamplesGallery,
} from './examples-gallery.js';
import { getDoc } from './editor-view.js';
import { readThemeKey, themeChanges } from './theme-yaml.js';
import { escapeHtml } from './dom.js';
import { applyDiagnostics } from 'codemirror-dbt-charts';
import { sendRender } from './websocket.js';


export function resizeSvgIframe() {
    const iframe = document.getElementById('svg-preview-iframe');
    if (!iframe) return;
    try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        const svg = doc && doc.querySelector('svg');
        if (svg) {
            const height = Math.ceil(svg.getBoundingClientRect().height);
            iframe.style.height = height + 'px';
            iframe.style.minHeight = '0';
        }
    } catch (e) {
        console.debug('Unable to size SVG iframe:', e.message);
    }
}

// Sync letterbox state from the parent body classes into the iframe's inner
// body so the inner CSS rules (body.letterbox / body.letterbox svg) apply.
// Called on iframe load, fullscreenchange, and letterbox toggle. The pillarbox
// color flows through the --pillarbox-bg CSS variable, set inline on the inner
// body in the iframe-load handler (formatContent) by reading the SVG's first
// <rect fill> child.
export function syncIframeLetterboxState() {
    const iframe = document.getElementById('svg-preview-iframe');
    if (!iframe) return;
    try {
        const innerBody = iframe.contentDocument?.body;
        if (!innerBody) return;
        const on = document.body.classList.contains('aspect-lock-mode')
            && document.body.classList.contains('fullscreen-mode');
        innerBody.classList.toggle('letterbox', on);
    } catch (e) {
        // cross-origin or document not ready — ignore
    }
}

export function scalePreviewContent() {
    const previewContent = document.getElementById('preview-content');
    if (!previewContent) return;

    const format = document.getElementById('format-selector')?.value;
    if (format === 'terminal') {
        const wrapper = previewContent.querySelector('.preview-content-wrapper');
        if (wrapper) {
            wrapper.style.transform = 'scale(1)';
            wrapper.style.height = 'auto';
            wrapper.style.width = '';
        }
        return;
    }

    let contentWrapper = previewContent.querySelector('.preview-content-wrapper');

    if (!contentWrapper) {
        const children = Array.from(previewContent.children);
        if (children.length === 0) return;

        const toWrap = children.filter(child =>
            !child.classList.contains('error-container') &&
            !child.classList.contains('preview-placeholder') &&
            !child.classList.contains('warnings-strip') &&
            !child.classList.contains('unranged-diagnostics-strip')
        );

        if (toWrap.length === 0) return;

        contentWrapper = document.createElement('div');
        contentWrapper.className = 'preview-content-wrapper';

        const firstToWrap = toWrap[0];
        previewContent.insertBefore(contentWrapper, firstToWrap);

        toWrap.forEach(el => contentWrapper.appendChild(el));
    }

    const containerWidth = previewContent.clientWidth;

    contentWrapper.style.transform = 'scale(1)';
    contentWrapper.style.width = '';

    void contentWrapper.offsetWidth;

    const contentWidth = contentWrapper.scrollWidth;

    if (contentWidth > containerWidth && containerWidth > 0) {
        const scale = containerWidth / contentWidth;
        contentWrapper.style.transform = `scale(${scale})`;
        contentWrapper.style.width = `${contentWidth}px`;
        const originalHeight = contentWrapper.scrollHeight;
        contentWrapper.style.height = `${originalHeight * scale}px`;
    } else {
        contentWrapper.style.transform = 'scale(1)';
        contentWrapper.style.height = 'auto';
        contentWrapper.style.width = '';
    }
}

// Resolved asset URLs for the preview iframe enhancements.  Populated by
// initPreviewAssets() at startup; falls back to the unhashed dev paths when
// the Vite manifest is absent (dev mode, no build step).
const _previewAssets = {
    chatCss: null,
    chatJs: null,
    svgWrapperCss: null,
};

// Fetch the Vite manifest and populate _previewAssets with fingerprinted URLs.
// Returns a promise that resolves when the assets are ready (or immediately if
// already resolved).  Concurrent callers share the same in-flight promise.
let _manifestPromise = null;
export function initPreviewAssets() {
    if (_manifestPromise) return _manifestPromise;
    return (_manifestPromise = (async () => {
        try {
            const resp = await fetch('/static/dist/.vite/manifest.json');
            if (!resp.ok) return;
            const manifest = await resp.json();
            const base = window.location.origin + '/static/dist/';
            for (const [key, entry] of Object.entries(manifest)) {
                const file = entry.file;
                if (key === 'static/preview-chat.js') {
                    _previewAssets.chatJs = base + file;
                } else if (key === 'static/preview-chat.css') {
                    _previewAssets.chatCss = base + file;
                } else if (key === 'static/preview-svg-wrapper.css') {
                    _previewAssets.svgWrapperCss = base + file;
                }
            }
        } catch (_) {
            // manifest absent in dev mode — unhashed fallback paths are used
        }
    })());
}

export function injectPlaygroundEnhancements(htmlContent) {
    const linkTag = `<link rel="stylesheet" href="${_previewAssets.chatCss ?? (window.location.origin + '/static/preview-chat.css')}">`;
    const scriptTag = `<script src="${_previewAssets.chatJs ?? (window.location.origin + '/static/preview-chat.js')}"></script>`;

    let enhanced = htmlContent;

    if (enhanced.includes('</head>')) {
        enhanced = enhanced.replace('</head>', linkTag + '</head>');
    } else if (enhanced.includes('<body')) {
        enhanced = enhanced.replace('<body', linkTag + '<body');
    }

    if (enhanced.includes('</body>')) {
        enhanced = enhanced.replace('</body>', scriptTag + '</body>');
    } else {
        enhanced += scriptTag;
    }

    enhanced = enhanced.replace(/\{actions:\s*false\}/g, '{actions: {editor: false}}');

    return enhanced;
}

// A rendered SVG board as a standalone document for a blob iframe. The board
// carries its own controls (the server draws them), so the document adds only
// behavior: the runtime that binds them and the styles its popovers need. Both
// are linked rather than inlined so a re-render on every keystroke doesn't
// resend them, and both ship unconditionally: the runtime also intercepts the
// <a href="?..."> links a tabbed board renders, which a blob URL cannot navigate
// to, and the styles are needed the moment any control opens.
function svgPreviewDocument(content) {
    const origin = window.location.origin;
    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <link rel="stylesheet" href="${origin}/static/fonts/_font_face.css">
    <link rel="stylesheet" href="${_previewAssets.svgWrapperCss ?? (origin + '/static/preview-svg-wrapper.css')}">
    <link rel="stylesheet" href="${origin}/api/controls/styles.css">
</head>
<body><div data-dbt-board style="position: relative">${cleanSvg(content)}<script src="${origin}/api/controls/runtime.js"></script></div></body>
</html>`;
}

export function createBlobIframe(htmlContent, iframeId, minHeight) {
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);

    const storeKey = '_last' + iframeId + 'BlobUrl';
    if (window[storeKey]) URL.revokeObjectURL(window[storeKey]);
    window[storeKey] = blobUrl;

    return '<iframe id="' + iframeId + '" src="' + blobUrl + '" style="width: 100%; min-height: ' + minHeight + 'px; border: none;"></iframe>';
}

// The rendered SVG can carry ns0:-style namespace prefixes; the iframe's
// HTML parser wants plain SVG element names.
function cleanSvg(content) {
    return content
        .replace(/\s*xmlns:ns\d+="[^"]*"/g, '')
        .replace(/<(\/?)(ns\d+):([a-zA-Z][a-zA-Z0-9-]*)/g, '<$1$3')
        .replace(/<\/(ns\d+):([a-zA-Z][a-zA-Z0-9-]*>)/g, '</$2');
}

function syncPillarboxBg(iframe) {
    const previewContent = document.getElementById('preview-content');
    const innerBody = iframe.contentDocument?.body;
    const svgEl = iframe.contentDocument?.querySelector('svg');
    const bg = svgEl?.querySelector(':scope > rect[fill]')?.getAttribute('fill');
    if (bg) {
        previewContent.style.setProperty('--pillarbox-bg', bg);
        if (innerBody) innerBody.style.setProperty('--pillarbox-bg', bg);
    } else {
        previewContent.style.removeProperty('--pillarbox-bg');
        if (innerBody) innerBody.style.removeProperty('--pillarbox-bg');
    }
}

let _svgSizeObserver = null;
function observeSvgSize(iframe) {
    _svgSizeObserver?.disconnect();
    _svgSizeObserver = null;
    const svgEl = iframe.contentDocument?.querySelector('svg');
    if (svgEl && typeof ResizeObserver !== 'undefined') {
        _svgSizeObserver = new ResizeObserver(() => resizeSvgIframe());
        _svgSizeObserver.observe(svgEl);
    }
}

// Swaps a render into the live board, keeping the iframe (and its runtime, open
// menus and scroll). The container must be a fresh node: the runtime wires hover
// highlighting once per board element, keyed on its `dbt-interactive` class.
// Returns false when there is no live board to swap into.
function swapBoardInPlace(content, stripsHtml) {
    const iframe = document.getElementById('svg-preview-iframe');
    const container = iframe?.contentDocument?.querySelector('[data-dbt-board]');
    if (!container) return false;
    const board = iframe.contentDocument.createElement('div');
    board.setAttribute('data-dbt-board', '');
    board.style.position = 'relative';
    board.innerHTML = cleanSvg(content);
    container.replaceWith(board);
    iframe.contentWindow.dbtControls.mount(board);
    while (iframe.nextSibling) iframe.nextSibling.remove();
    iframe.insertAdjacentHTML('afterend', stripsHtml);
    syncPillarboxBg(iframe);
    observeSvgSize(iframe);
    resizeSvgIframe();
    return true;
}

// Clear the runtime's per-chart spinners when a swap-in-place render won't
// land. The runtime script loads async, so it may not be there yet.
export function clearBoardLoading() {
    document.getElementById('svg-preview-iframe')?.contentWindow?.__dfClearChartLoading?.();
}

export function formatContent(content, format) {
    const previewContent = document.getElementById('preview-content');

    if (format === 'html') {
        previewContent.classList.add('html-mode');
    } else {
        previewContent.classList.remove('html-mode');
    }

    if (format === 'svg') {
        const iframeHtml = createBlobIframe(svgPreviewDocument(content), 'svg-preview-iframe', 0);

        const savedScrollTop = previewContent.scrollTop;

        setTimeout(() => {
            const iframe = document.getElementById('svg-preview-iframe');
            if (!iframe) return;

            iframe.addEventListener('load', () => {
                resizeSvgIframe();
                try {
                    syncPillarboxBg(iframe);
                    syncIframeLetterboxState();
                    // Reactive iframe sizing: any time the inner SVG reflows
                    // (fullscreen entry, letterbox toggle, pane drag, window
                    // resize), the observer fires and the iframe height tracks
                    // the SVG's new height. Replaces fragile setTimeout/rAF
                    // measurement that fired before the inner document had
                    // finished laying out. Setting iframe height does not change
                    // iframe width DIRECTLY — but it can indirectly, by pushing
                    // #preview-content across its scroll threshold and summoning
                    // a scrollbar. `scrollbar-gutter: stable` on #preview-content
                    // is what closes that path; without it this observer
                    // oscillates every frame on boards near the threshold.
                    observeSvgSize(iframe);
                } catch (e) {
                    // cross-origin or CSP restriction — leave CSS fallback in place
                }
                if (savedScrollTop > 0) {
                    previewContent.scrollTop = savedScrollTop;
                }
            });
        }, 0);

        return iframeHtml;
    } else if (format === 'png') {
        if (content.trim().startsWith('<')) {
            return content;
        } else {
            return `<div style="width: 100%; text-align: center;"><img src="data:image/png;base64,${content}" style="max-width: 100%; height: auto;" alt="Dashboard preview" /></div>`;
        }
    } else if (format === 'html') {
        const enhancedContent = injectPlaygroundEnhancements(content);

        const iframeHtml = createBlobIframe(enhancedContent, 'html-preview-iframe', 2000);

        const savedScrollTop = previewContent.scrollTop;

        setTimeout(() => {
            const iframe = document.getElementById('html-preview-iframe');
            if (!iframe) return;

            function updateIframeHeight() {
                try {
                    const doc = iframe.contentDocument || iframe.contentWindow.document;
                    if (!doc || !doc.body) return;

                    const height = Math.max(
                        doc.body.scrollHeight,
                        doc.body.offsetHeight,
                        doc.documentElement.clientHeight,
                        doc.documentElement.scrollHeight,
                        doc.documentElement.offsetHeight
                    );
                    iframe.style.height = height + 'px';
                    iframe.style.minHeight = 'auto';
                } catch (e) {
                    console.debug('Unable to access iframe content:', e.message);
                }
            }

            iframe.addEventListener('load', () => {
                updateIframeHeight();
                if (savedScrollTop > 0) {
                    previewContent.scrollTop = savedScrollTop;
                }

                try {
                    const doc = iframe.contentDocument || iframe.contentWindow.document;
                    if (doc && doc.documentElement) {
                        const observer = new MutationObserver(updateIframeHeight);
                        observer.observe(doc.documentElement, {
                            childList: true,
                            subtree: true,
                            attributes: true
                        });
                    }
                } catch (e) {
                    console.debug('Unable to observe iframe changes:', e.message);
                }
            });

            window.addEventListener('resize', updateIframeHeight);
        }, 0);

        return iframeHtml;
    } else {
        return content;
    }
}

export function updatePreview(content, errors, format = 'svg', warnings = []) {
    // The lint adapter never formats/fetches a doc URL itself — the
    // page's shipped code -> {title, doc_url} map
    // (state.playgroundConfig.diagnostic_display)
    // is passed straight through. Runs before any editor exists yet (e.g.
    // the render kicked off while initEditor() is still loading its catalog).
    let unrangedDiagnostics = [];
    if (state.editor) {
        // websocket.js's transport-error path calls updatePreview('', [message])
        // with a bare string, not a Diagnostic — skip it rather than spreading a
        // string into a character-indexed object.
        const diagnostics = [...errors, ...warnings]
            .filter((d) => typeof d === 'object' && d !== null);
        // The server prefixes examples with "charts/", matching SourceRange.file.
        // Pass "" when no example is loaded (no file path = no cross-file filtering).
        const docFile = state.currentExamplePath
            ? `charts/${state.currentExamplePath}`
            : '';
        unrangedDiagnostics = applyDiagnostics(
            state.editor,
            diagnostics,
            state.playgroundConfig.diagnostic_display ?? {},
            docFile,
        );
    }

    const previewContent = document.getElementById('preview-content');
    if (state.shouldResetScrollOnNextUpdate) {
        previewContent.scrollTop = 0;
        state.setShouldResetScrollOnNextUpdate(false);
    }
    const isCodeFormat = state.CODE_FORMATS.has(format);
    const hasContent = !!(content && content.trim());
    const hasErrors = !!(errors && errors.length > 0);
    // Render warnings annotate the board, so they sit under it. Above it they
    // shove the whole preview down the pane on every keystroke that introduces
    // or clears one. Local-mode only — the server sends none when deployed.
    const warningsStripHtml = isCodeFormat ? '' : buildWarningsStripHtml(warnings);

    if (hasContent) {
        showVisualPreview();
        previewContent.classList.remove('html-mode');
        const stripsHtml = warningsStripHtml
            + buildUnrangedDiagnosticsStripHtml(unrangedDiagnostics);
        if (isCodeFormat) {
            showCodePreview(content, format);
        } else if (!(state.renderKeepsBoard && format === 'svg'
                     && swapBoardInPlace(content, stripsHtml))) {
            previewContent.innerHTML = formatContent(content, format) + stripsHtml;
        }
        if (hasErrors) {
            updatePreviewStatus(`Rendered with ${errors.length} chart error(s)`, 'rendered');
        } else {
            updatePreviewStatus(`Rendered (${format.toUpperCase()})`, 'rendered');
        }
        setTimeout(() => scalePreviewContent(), 0);
        return;
    }

    if (hasErrors) {
        showVisualPreview();
        previewContent.classList.remove('html-mode');
        previewContent.innerHTML = buildErrorContainerHtml(errors) + warningsStripHtml;
        updatePreviewStatus('Error', 'error');
        setTimeout(() => scalePreviewContent(), 0);
        return;
    }

    showVisualPreview();
    previewContent.classList.remove('html-mode');
    previewContent.innerHTML = '<div class="preview-placeholder">No preview available</div>';
    updatePreviewStatus('Empty', 'waiting');
}

export function applyReviewWidth(width) {
    if (!width) return;
    const input = document.getElementById('review-width-input');
    if (input) {
        input.value = width;
    }
    snapPreviewPaneWidth(parseInt(width, 10));
}

export function togglePreviewFullscreen() {
    if (document.fullscreenElement) {
        document.exitFullscreen();
    } else {
        document.documentElement.requestFullscreen().catch(() => {});
    }
}

// Clear the preview pane and show a spinner the instant a render is triggered,
// so a stale board never sits next to freshly-changed YAML. updatePreview()
// overwrites this when the render (HTTP or WebSocket) completes.
export function showPreviewLoading() {
    const previewContent = document.getElementById('preview-content');
    if (!previewContent) return;
    showVisualPreview();
    previewContent.classList.remove('html-mode');
    previewContent.innerHTML =
        '<div class="preview-loading"><div class="preview-spinner"></div></div>';
}

// The spinner showPreviewLoading() paints is cleared by exactly one thing:
// updatePreview() on a completed render. A render that never lands — the handler
// threw, the socket dropped — would leave it turning until the tab is reloaded,
// which reads as a hung server. Say what happened instead.
export function failPreview(message) {
    const previewContent = document.getElementById('preview-content');
    if (!previewContent) return;
    showVisualPreview();
    previewContent.innerHTML =
        `<div class="preview-placeholder">${escapeHtml(message)}</div>`;
}

// Whether a render is currently in flight on screen. Callers that are reporting
// something about the *connection* rather than about a render — a dropped socket
// — use this to leave a board alone: it is still the last good render, and losing
// the socket says nothing about it.
export function isPreviewLoading() {
    return !!document.getElementById('preview-content')?.querySelector('.preview-loading');
}

const THEME_TITLE = "Set the board's theme: key in the YAML";
const INHERITED_TITLE = 'This board inherits from another board; set its theme in the YAML';

// The theme selector mirrors the YAML's top-level `theme:`/`extends:` key. It
// locks, rather than overwrite, an `extends:` naming anything the selector does
// not list (a board, an unknown name) and any key whose value is not a one-line
// scalar (a block list of parents). A `theme:` the selector does not list shows
// the blank placeholder.
function syncThemeSelector(yaml) {
    const selector = document.getElementById('theme-selector');
    if (!selector) {
        return;
    }
    const found = readThemeKey(yaml);
    const listed = !!found?.value && [...selector.options].some((o) => o.value === found.value);
    const inherited = !!found && (!found.scalar || (found.key === 'extends' && !listed));
    selector.disabled = inherited;
    selector.title = inherited ? INHERITED_TITLE : THEME_TITLE;
    selector.options[0].textContent = inherited ? 'Theme: inherited' : 'Theme';
    selector.value = listed && !inherited ? found.value : '';
}

// Fill the theme selector from the server's built-in theme list. The fetch
// races the first render, so this also owns the first sync to the YAML.
export async function populateThemeSelector() {
    const selector = document.getElementById('theme-selector');
    if (!selector) {
        return;
    }
    try {
        const names = await fetch('/api/themes').then((r) => r.json());
        const existing = new Set([...selector.options].map((o) => o.value));
        for (const name of names) {
            if (!existing.has(name)) {
                const option = document.createElement('option');
                option.value = name;
                option.textContent = name;
                selector.appendChild(option);
            }
        }
    } catch (error) {
        console.error('Error loading themes for selector:', error);
        return;
    }
    syncThemeSelector(getDoc(state.editor));
}

// `keepBoard` leaves the SVG board on screen and swaps the render into it;
// otherwise the pane blanks to a spinner until the render lands.
export function sendYAMLToPreview({ keepBoard = false } = {}) {
    const yaml = getDoc(state.editor);
    syncThemeSelector(yaml);
    const format = document.getElementById('format-selector').value || 'svg';

    if (!yaml.trim()) {
        showInlineExamplesGallery();
        return;
    }

    const swap = keepBoard && format === 'svg'
        && !!document.getElementById('svg-preview-iframe');
    state.setRenderKeepsBoard(swap);
    if (!swap) showPreviewLoading();

    if (state.ws && state.ws.readyState === WebSocket.OPEN) {
        const msg = attachTerminalSize({
            yaml,
            format,
            variables: state.currentVariables,
            current_file_path: state.currentExamplePath || null,
        }, format);
        sendRender(msg);
        updatePreviewStatus('Rendering...', 'rendering');
    } else {
        renderViaHTTP(yaml, format);
    }
}

export async function renderViaHTTP(yaml, format = 'svg') {
    try {
        updatePreviewStatus('Rendering...', 'rendering');
        const payload = attachTerminalSize({
            yaml,
            format,
            variables: state.currentVariables,
        }, format);
        const response = await fetch(state.withProject('/api/render'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const result = await response.json();
        const content = result[format] || result.html || result.svg || result.png || result.pdf || '';
        state.updatePreview(
            content, result.errors || [], format, result.warnings || []
        );
    } catch (error) {
        state.updatePreview('', [`Error: ${error.message}`], format);
    }
}

export function resetVariables() {
    state.setCurrentVariables({});
}

/** The loaded example a board href names, and the query string it carries. */
function exampleForHref(href) {
    const qIndex = href.indexOf('?');
    const path = qIndex === -1 ? href : href.slice(0, qIndex);
    const slug = normalizeExamplePath(path.replace(/^\//, ''));
    const examplePath = /\.ya?ml$/.test(slug) ? slug : `${slug}.yml`;
    return {
        example: state.examplesData.find((ex) => ex.path === examplePath),
        params: new URLSearchParams(qIndex === -1 ? '' : href.slice(qIndex + 1)),
    };
}

/**
 * Maps a `dbt-board-navigate` href to a loaded example the same way a
 * gallery click would, carrying the query string as its initial variables.
 */
function handleBoardNavigate(href) {
    const { example, params } = exampleForHref(href);
    if (!example) {
        console.error(`link: pointed at an unknown board: ${href}`);
        return;
    }
    const variables = {};
    params.forEach((value, name) => { variables[name] = value; });
    if (Object.keys(variables).length) {
        state.setUrlVariables(variables);
    }
    selectExampleFromGallery(example, closeExamplesModal);
}

/** The playground page that opens the example a board href names. */
function exampleUrlForHref(href) {
    const { example, params } = exampleForHref(href);
    if (!example) throw new Error(`link: pointed at an unknown board: ${href}`);
    const query = new URLSearchParams();
    const project = state.activeProject();
    if (project) query.set('project', project);
    query.set('example', example.path);
    const variables = {};
    params.forEach((value, name) => { variables[name] = value; });
    if (Object.keys(variables).length) query.set('vars', JSON.stringify(variables));
    return `${window.location.origin}${window.location.pathname}?${query}`;
}

let linkModal = null;

// The clicked mark's box moved from the preview frame's viewport into this
// page's, scaled by however much the preview is zoomed.
function markInPage(event) {
    const frame = [...document.querySelectorAll('iframe')]
        .find((candidate) => candidate.contentWindow === event.source);
    const rect = event.data.rect;
    if (!frame || !rect) return null;
    const box = frame.getBoundingClientRect();
    const zoom = frame.offsetWidth ? box.width / frame.offsetWidth : 1;
    return {
        left: box.left + rect.left * zoom,
        top: box.top + rect.top * zoom,
        width: rect.width * zoom,
        height: rect.height * zoom,
    };
}
let linkModalBlobUrl = null;
let linkModalRenders = 0;

/**
 * Renders the example a modal link names into a blob URL for the link modal
 * (link_modal.js). A repeated param is a list variable, as on a server.
 */
async function renderBoardForModal(href) {
    const render = ++linkModalRenders;
    const { example, params } = exampleForHref(href);
    let html;
    if (!example) {
        html = errorDocument([`link: pointed at an unknown board: ${href}`]);
    } else {
        const variables = {};
        params.forEach((value, name) => {
            const all = params.getAll(name);
            variables[name] = all.length > 1 ? all : value;
        });
        html = await renderedExample(example, variables);
    }
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    // Only the newest render is shown (the modal drops the rest), so only it
    // replaces the frame's URL; a slower, older one frees just its own.
    if (render !== linkModalRenders) {
        URL.revokeObjectURL(url);
        return url;
    }
    if (linkModalBlobUrl) URL.revokeObjectURL(linkModalBlobUrl);
    linkModalBlobUrl = url;
    return url;
}

/** The example rendered as a page, or a page naming why it could not be. */
async function renderedExample(example, variables) {
    const source = await fetch(state.withProject(`/api/examples/${example.path}`));
    if (!source.ok) {
        return errorDocument([`link: could not load ${example.path}: HTTP ${source.status}`]);
    }
    const { yaml } = await source.json();
    const response = await fetch(state.withProject('/api/render'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ yaml, format: 'svg', variables }),
    });
    // A failed render names its errors in JSON; anything else is a server fault.
    if (!response.ok && !(response.headers.get('content-type') || '').includes('json')) {
        return errorDocument([`link: could not render ${example.path}: HTTP ${response.status}`]);
    }
    const result = await response.json();
    return result.svg ? svgPreviewDocument(result.svg) : errorDocument(result.errors || []);
}

function errorDocument(errors) {
    const pre = document.createElement('pre');
    pre.textContent = errors.map((e) => (typeof e === 'string' ? e : e.message)).join('\n');
    return `<!DOCTYPE html><html><body>${pre.outerHTML}</body></html>`;
}

export function initPreview() {
    let resizeTimeout = null;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            scalePreviewContent();
            resizeSvgIframe();
        }, 100);
    });

    document.getElementById('format-selector').addEventListener('change', () => {
        updateUrlParams();
        sendYAMLToPreview();
    });

    populateThemeSelector();
    const themeSelector = document.getElementById('theme-selector');
    if (themeSelector) {
        // A plain edit, so the editor's own change path re-renders.
        themeSelector.addEventListener('change', () => {
            const doc = getDoc(state.editor);
            const changes = themeChanges(doc, themeSelector.value);
            if (changes.length === 0) {
                syncThemeSelector(doc);
                return;
            }
            state.editor.dispatch({ changes });
        });
    }

    document.getElementById('aspect-lock-toggle').addEventListener('change', (e) => {
        document.body.classList.toggle('aspect-lock-mode', e.target.checked);
        syncIframeLetterboxState();
        // Toggling letterbox changes the inner SVG's max-width, which reflows
        // the SVG to a new height. The iframe must be re-measured after layout
        // settles or it keeps its old height.
        requestAnimationFrame(() => {
            scalePreviewContent();
            resizeSvgIframe();
        });
    });

    document.getElementById('review-width-input').addEventListener('change', (e) => {
        const raw = e.target.value.trim();
        const width = raw === '' ? '' : String(parseInt(raw, 10));
        applyReviewWidth(width);
        window.localStorage.setItem(state.REVIEW_WIDTH_STORAGE_KEY, width);
        setTimeout(() => {
            refreshPaneLayout(
                sendYAMLToPreview,
                scalePreviewContent,
                resizeSvgIframe,
            );
        }, 0);
    });

    document.getElementById('fullscreen-btn').addEventListener('click', () => {
        togglePreviewFullscreen();
    });

    document.addEventListener('fullscreenchange', () => {
        const entering = !!document.fullscreenElement;
        document.body.classList.toggle('fullscreen-mode', entering);
        if (entering) {
            const lock = document.getElementById('aspect-lock-toggle');
            lock.checked = true;
            document.body.classList.add('aspect-lock-mode');
        }
        const widthInput = document.getElementById('review-width-input');
        if (widthInput) widthInput.disabled = entering;
        syncIframeLetterboxState();
        // Wait for fullscreen layout to settle before measuring the SVG.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                scalePreviewContent();
                resizeSvgIframe();
            });
        });
    });

    window.addEventListener('message', function(event) {
        // The modal's board talks to link_modal.js, never to the preview.
        if (linkModal && linkModal.owns(event.source)) return;
        if (event.data && event.data.type === 'dbt-variable-change') {
            // null means "the default": the variable is dropped, not stored.
            const merged = { ...state.currentVariables, ...event.data.variables };
            state.setCurrentVariables(
                Object.fromEntries(Object.entries(merged).filter(([, value]) => value !== null)),
            );
            updateUrlParams();
            sendYAMLToPreview({ keepBoard: true });
        } else if (event.data && event.data.type === 'dbt-board-navigate') {
            handleBoardNavigate(event.data.href);
        } else if (event.data && event.data.type === 'dbt-board-open-tab') {
            window.open(exampleUrlForHref(event.data.href), '_blank', 'noopener,noreferrer');
        } else if (event.data && event.data.type === 'dbt-board-open-modal') {
            linkModal ??= window.dbtLinkModal.create(renderBoardForModal, exampleUrlForHref);
            linkModal.open(event.data.href, markInPage(event), event.data.open);
        }
    });
}
